// Which objects a pin holds across a preset load, and what shape they arrive in.
import { setProperty } from 'dot-prop'
import { produce } from 'immer'
import { parseLivePath, vocabPinId } from '~/lib/params'
import { objectProps } from '~/lib/presetSettings'
import { isShaderSource } from '~/lib/shaders'

const MAX_DEPTH = 16
const EMPTY = new Set()

/** Never pinned: identity and the tree's links travel with existence. */
const STRUCTURE = new Set(['id', 'type', 'parentId', 'children', 'mask', 'maskFor'])

const GEOMETRY = new Set(['data', 'x', 'y', 'width', 'height', 'rotation', 'scaleX', 'scaleY', 'pivotX', 'pivotY'])

/** The layer row's pin menu, widest first. */
export const PIN_MODES = ['all', 'geometry', 'existence']

/** Every pin key of `id`'s subtree under `mode`: existence, plus each schema prop (`all`) or each geometry prop (`geometry`). */
export const objectPinKeys = (objects, id, mode = 'all', out = [], depth = 0) => {
	const obj = objects?.[id]
	if (!obj || depth > MAX_DEPTH) return out
	out.push(`objects.${id}`)
	if (mode !== 'existence')
		for (const k in objectProps(obj.type))
			if (!STRUCTURE.has(k) && (mode === 'all' || GEOMETRY.has(k))) out.push(`objects.${id}.${k}`)
	for (const cid of obj.children ?? []) objectPinKeys(objects, cid, mode, out, depth + 1)
	return out
}

/** The pin keys held under `id`'s subtree. */
export const subtreePins = (objects, pins, id) => {
	const ids = new Set(objectPinKeys(objects, id, 'existence').map(k => k.slice(8)))
	return pins.filter(k => {
		const p = parseLivePath(k)
		return p?.scope === 'object' && ids.has(p.id)
	})
}

/** The menu mode `id`'s subtree pins match: null unpinned, `'custom'` for any other mix. */
export const objectPinMode = (objects, pins, id) => {
	const held = new Set(subtreePins(objects, pins, id))
	if (!held.size) return null
	for (const mode of PIN_MODES) {
		const keys = objectPinKeys(objects, id, mode)
		if (keys.length === held.size && keys.every(k => held.has(k))) return mode
	}
	return 'custom'
}

/** Each existence-pinned object's pinned props (`['opacity']`). */
const pinnedPropsById = pins => {
	const out = new Map()
	for (const key of pins) {
		const p = parseLivePath(key)
		if (p?.scope !== 'object') continue
		if (!out.has(p.id)) out.set(p.id, [])
		if (!p.existence) out.get(p.id).push([p.key])
	}
	return out
}

const withPath = (obj, path, from) => {
	const v = path.reduce((n, k) => n?.[k], from)
	if (v !== undefined) {
		setProperty(obj, path.join('.'), structuredClone(v))
		return
	}
	// Absent is at-initial, and that is the pinned value.
	const parent = path.slice(0, -1).reduce((n, k) => n?.[k], obj)
	if (parent) delete parent[path[path.length - 1]]
}

/** A layer entry as an object: identity and pinned values, an unread (null) value absent. */
const fromLayer = (layer, id) => {
	const entry = layer?.objects?.[id]
	if (!entry?.type) return undefined
	const obj = { id }
	for (const k in entry) if (entry[k] !== null) obj[k] = entry[k]
	return obj
}

const below = (order, pinned, id) => order.slice(0, order.indexOf(id)).filter(o => !pinned.has(o)).length

/** `'bottom'` when no unpinned root object sits under `id` and some sit over it; otherwise `'top'`. */
const bandIn = (order, pinned, id) => {
	const unpinned = order.filter(o => !pinned.has(o)).length
	const under = below(order, pinned, id)
	return under === 0 && unpinned > 0 ? 'bottom' : 'top'
}

/** Root order with every pinned object below or above every unpinned one, z among pinned kept. A pinned object keeps its `prev` band unless it is in `moved`: then the side with fewer unpinned objects wins. */
export const bandOrder = (order, pinned, prev, moved = EMPTY) => {
	const unpinned = order.filter(id => !pinned.has(id))
	if (!unpinned.length || unpinned.length === order.length) return order
	const bottom = []
	const top = []
	for (const id of order) {
		if (!pinned.has(id)) continue
		const under = below(order, pinned, id)
		const held = !moved.has(id) && prev?.includes(id) ? bandIn(prev, pinned, id) : null
		const band = held ?? (under < unpinned.length - under ? 'bottom' : 'top')
		;(band === 'bottom' ? bottom : top).push(id)
	}
	const next = [...bottom, ...unpinned, ...top]
	return next.every((id, i) => id === order[i]) ? order : next
}

/** Why pinning `id` is refused, or null: pins are root-level or a whole group, and land in a band. */
export const pinRefusal = (objects, pinned, id) => {
	const obj = objects?.[id]
	if (!obj) return 'missing'
	if ((obj.parentId ?? 'root') !== 'root') return 'not root-level'
	const order = objects.root?.children ?? []
	const unpinned = order.filter(o => !pinned.has(o) && o !== id).length
	const under = below(order, new Set([...pinned, id]), id)
	return under > 0 && under < unpinned ? 'unpinned objects above and below' : null
}

/** `pinned ?? preset ?? initial` per existence-pinned object: pinned props from live, the rest from `incoming` (the destination's `objects`). An object live lacks comes from the pin `layer`. A root-level entry carries its live `band`. */
export const carriedObjects = (liveObjects, pins, incoming, layer) => {
	if (!pins?.length) return []

	const pinned = pinnedPropsById(pins)
	const sources = {}
	for (const id of pinned.keys()) {
		const obj = liveObjects?.[id] ?? fromLayer(layer, id)
		if (obj) sources[id] = obj
	}
	const ids = new Set(Object.keys(sources))
	if (!ids.size) return []

	// Parents-first: a child attached before its parent would be re-homed to root by a parent that was about to arrive.
	const ordered = []
	const seen = new Set()
	const visit = (id, depth = 0) => {
		if (depth > MAX_DEPTH || seen.has(id) || !ids.has(id)) return
		const parentId = sources[id].parentId
		if (parentId && parentId !== 'root') visit(parentId, depth + 1)
		if (seen.has(id)) return
		seen.add(id)
		ordered.push(id)
	}
	for (const id of ids) visit(id)

	const liveOrder = liveObjects?.root?.children ?? []
	return ordered.map(id => {
		const live = sources[id]
		const band = liveOrder.includes(id) ? bandIn(liveOrder, ids, id) : 'top'
		const preset = incoming?.[id]
		const obj = preset?.type === live.type ? structuredClone(preset) : { id, type: live.type }
		obj.parentId = live.parentId
		for (const path of pinned.get(id)) withPath(obj, path, live)
		// A mask link travels with existence when both halves are carried; otherwise the preset's stands, and `applyCarry` re-pairs it.
		for (const key of ['mask', 'maskFor']) if (live[key] && ids.has(live[key])) obj[key] = live[key]
		// The preset's own children stay; the carry re-attaches the carried ones.
		if (obj.children || live.children)
			obj.children = (obj.children ?? []).filter(cid => incoming?.[cid] && !ids.has(cid))
		return { id, obj, band, z: liveOrder.indexOf(id) }
	})
}

/** What a preset stores of `settings`: every pinned value dropped. A pinned object keeps its entry and its unpinned props. */
export const withoutPinned = (settings, pins) => {
	if (!pins?.length || !settings) return settings
	return produce(settings, d => {
		for (const key of pins) {
			const id = vocabPinId(key)
			// A vocabulary pin holds its id on every shader.
			if (id) {
				for (const source in d.source_settings ?? {}) {
					const bag = d.source_settings[source]
					if (!isShaderSource(source) || !(id in bag)) continue
					delete bag[id]
					if (!Object.keys(bag).length) delete d.source_settings[source]
				}
				continue
			}
			const p = parseLivePath(key)
			if (!p || p.existence) continue
			const [bag, parent] =
				p.scope === 'effect'
					? [d[p.effect], d]
					: p.scope === 'source'
						? [d.source_settings?.[p.source], d.source_settings]
						: [d.objects?.[p.id], null]
			if (!bag || !(p.key in bag)) continue
			delete bag[p.key]
			// An emptied section or source bag goes: absence is its one spelling.
			if (parent && !Object.keys(bag).length) delete parent[p.scope === 'effect' ? p.effect : p.source]
		}
		if (d.source_settings && !Object.keys(d.source_settings).length) delete d.source_settings
	})
}

/** `settings` with nothing a pin holds, for the dirty check: pinned values dropped as a save drops them, and an existence-pinned object reduced to its unpinned props, unlinked, since a load carries it either way. */
export const unpinnedSettings = (settings, pins) => {
	const out = withoutPinned(settings, pins)
	const ids = new Set()
	for (const key of pins ?? []) {
		const p = parseLivePath(key)
		if (p?.scope === 'object' && p.existence) ids.add(p.id)
	}
	if (!ids.size || !out) return out
	return produce(out, d => {
		d.objects ??= {}
		for (const id of ids) {
			const obj = d.objects[id] ?? {}
			const props = {}
			for (const k in obj) if (!STRUCTURE.has(k)) props[k] = obj[k]
			d.objects[id] = props
		}
		for (const oid in d.objects) {
			const children = d.objects[oid].children
			if (children?.some(cid => ids.has(cid))) d.objects[oid].children = children.filter(cid => !ids.has(cid))
		}
	})
}

/** ⚠️ A carried object whose parent is absent is re-homed to `root`, so its group-local `x`/`y` are reinterpreted as world and it lands offset. */
export const applyCarry = (objects, carry) => {
	for (const { id, obj } of carry ?? []) {
		// A carried child must not stay claimed by a parent the preset kept.
		for (const other in objects) {
			const children = objects[other]?.children
			if (other !== id && children?.includes(id)) {
				objects[other] = { ...objects[other], children: children.filter(cid => cid !== id) }
			}
		}
		const parentId = obj.parentId && objects[obj.parentId] ? obj.parentId : 'root'
		objects[id] = { ...obj, parentId }
		const parent = objects[parentId]
		if (!parent) continue
		const children = parent.children ?? []
		if (!children.includes(id)) objects[parentId] = { ...parent, children: [...children, id] }
	}
	if (!carry?.length) return objects
	pairMaskLinks(objects, new Set(carry.map(e => e.id)))
	placeBands(objects, carry)
	return objects
}

/** Root-level carried objects into their live band, in live z. */
const placeBands = (objects, carry) => {
	const root = objects.root
	if (!root?.children) return
	const atRoot = carry.filter(e => objects[e.id]?.parentId === 'root')
	if (!atRoot.length) return
	const byZ = (a, b) => (a.z < 0 ? Infinity : a.z) - (b.z < 0 ? Infinity : b.z)
	const band = name =>
		atRoot
			.filter(e => (e.band === 'bottom') === (name === 'bottom'))
			.sort(byZ)
			.map(e => e.id)
	const carried = new Set(atRoot.map(e => e.id))
	objects.root = {
		...root,
		children: [...band('bottom'), ...root.children.filter(id => !carried.has(id)), ...band('top')],
	}
}

/** Keep every mask link two-way after a carry: a preset partner still naming a carried object is re-paired with it, and any other stale back-reference is dropped. */
const pairMaskLinks = (objects, carriedIds) => {
	const drop = (id, key) => {
		const { [key]: _gone, ...rest } = objects[id]
		objects[id] = rest
	}
	for (const id of carriedIds) {
		for (const [key, back] of [
			['mask', 'maskFor'],
			['maskFor', 'mask'],
		]) {
			const partners = Object.keys(objects).filter(o => o !== id && objects[o]?.[back] === id)
			const own = objects[id][key]
			const keep = own && objects[own]?.[back] === id ? own : partners[0]
			if (keep && keep !== own) objects[id] = { ...objects[id], [key]: keep }
			else if (!keep && own) drop(id, key)
			for (const p of partners) if (p !== keep) drop(p, back)
		}
	}
}
